import { Link } from "react-router-dom";
import { Clock, ArrowRight, ChevronDown, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const SERVICE_IMAGES = {
  naturopatia: "/studio.webp",
  integrazione: "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=1600&q=80",
  prevenzione: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1600&q=80",
  massoterapia: "https://customer-assets.emergentagent.com/job_olistico-care/artifacts/mr7819fb_4079ce91-90f6-4cb2-ad92-8b74933a9efa.jpeg",
  microscopia: "https://images.unsplash.com/photo-1576086213369-97a306d36557?w=1600&q=80",
  coppettazione: "https://customer-assets.emergentagent.com/job_olistico-care/artifacts/byps50wh_WhatsApp%20Image%202026-06-08%20at%2010.13.10.jpeg",
};

function TreatmentsList({ treatments, serviceId }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2.5">
      {treatments.map((tr, k) => {
        const isOpen = open === k;
        return (
          <div
            key={k}
            data-testid={`treatment-${serviceId}-${k}`}
            className={`border rounded-2xl overflow-hidden transition-all ${
              isOpen ? "border-[#8A9A86] bg-[#F3F0EA]/60" : "border-[#E6E2D8] bg-white"
            }`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : k)}
              className="w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-[#F3F0EA]/40 transition-colors"
              data-testid={`treatment-toggle-${serviceId}-${k}`}
            >
              <span className="font-medium text-[#2C2F2A]">{tr.name}</span>
              <ChevronDown
                size={18}
                strokeWidth={1.5}
                className={`text-[#8A9A86] flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isOpen && (
              <div className="px-5 pb-5 pt-1 text-[#5C6356] leading-relaxed text-sm border-t border-[#E6E2D8]/60">
                {tr.description}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Servizi() {
  const { t } = useTranslation();
  const services = t("services.items", { returnObjects: true });

  return (
    <div data-testid="page-servizi" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="label-eyebrow mb-4">{t("services.eyebrow")}</div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#2C2F2A]">
          {t("services.title1")} <em className="text-[#8A9A86] font-light">{t("services.title2")}</em>.
        </h1>
        <p className="mt-8 text-lg text-[#5C6356] leading-relaxed">{t("services.intro")}</p>
      </div>

      <div className="mt-16 space-y-12 lg:space-y-16">
        {services.map((s, i) => (
          <div
            key={s.id}
            data-testid={`service-${s.id}`}
            className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-start ${
              i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
            }`}
          >
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <img
                src={SERVICE_IMAGES[s.id]}
                alt={s.name}
                className="w-full h-[380px] lg:h-[440px] object-cover rounded-[2rem]"
              />
            </div>
            <div className="lg:col-span-7 lg:px-4">
              <div className="label-eyebrow mb-3">{t("services.serviceLabel")} {String(i + 1).padStart(2, "0")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2F2A] leading-tight">{s.name}</h2>
              <p className="font-serif italic text-2xl text-[#8A9A86] mt-2">{s.tagline}</p>
              <p className="mt-6 text-[#5C6356] leading-relaxed text-lg">{s.description}</p>

              {s.treatments && s.treatments.length > 0 && (
                <div className="mt-8">
                  <div className="label-eyebrow mb-2">{t("services.treatmentsLabel")}</div>
                  <p className="text-sm text-[#5C6356] mb-5 italic">{t("services.treatmentsHelper")}</p>
                  <TreatmentsList treatments={s.treatments} serviceId={s.id} />
                </div>
              )}

              <div className="mt-6 flex items-center gap-2 text-sm text-[#5C6356]">
                <Clock size={14} strokeWidth={1.5} />
                <span>{t("services.duration")}: {s.duration}</span>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/prenotazioni" data-testid={`service-book-${s.id}`} className="btn-primary">
                  {t("services.bookService")} <ArrowRight size={16} strokeWidth={1.5} />
                </Link>
                {s.treatments && (
                  <a
                    href="https://wa.me/393482478661"
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`service-whatsapp-${s.id}`}
                    className="btn-secondary"
                  >
                    <MessageCircle size={16} strokeWidth={1.5} /> WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
